import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { Credential } from "../../graphql/types";
import { dummyPassword } from "../../lib/Util";
import { CheckboxWithLabel } from "../ui/CheckboxWithLabel";
import { KeyCell } from "./KeyCell";
import { TextFieldStyled } from "./KeyEditorCommon";
import { INPUT_PLACEHOLDERS, rsaPrivateKey } from "./KeyInputPlaceholders";

type KeyOrPasswordInputProps = {
  authType: string;
  control?: Control;
  errors?: FieldErrors;
  mode?: string;
  isRequired: boolean;
  credential?: Credential;
  passwordTestid: string;
  passwordFieldname: string;
  passwordKeyTestid: string;
  privateKeyFieldname: string;
};

const KeyOrPasswordInput = ({
  authType,
  control,
  errors,
  mode,
  isRequired,
  credential,
  passwordTestid,
  passwordFieldname,
  passwordKeyTestid,
  privateKeyFieldname,
}: KeyOrPasswordInputProps) => {
  const fieldName = authType === "password" ? passwordFieldname : privateKeyFieldname;
  const resolvePasswordPlaceholder = () => {
    if (
      authType === "password"
        ? credential?.properties_exist[passwordFieldname]
        : credential?.properties_exist[privateKeyFieldname]
    )
      return dummyPassword;
    return authType === "password" ? INPUT_PLACEHOLDERS.password : rsaPrivateKey;
  };

  return (
    <Controller
      key={fieldName}
      name={fieldName}
      control={control}
      rules={{ required: isRequired }}
      render={({ field: { onChange, value } }) => {
        if (mode === "view") {
          return <>{dummyPassword}</>;
        }

        return (
          <Stack data-testid="form-controller" spacing={1} sx={{ marginTop: 1 }}>
            <TextFieldStyled
              error={errors && errors[fieldName] ? true : false}
              required={isRequired}
              id={fieldName}
              placeholder={resolvePasswordPlaceholder()}
              type={authType === "password" ? "password" : "text"}
              disabled={mode === "view"}
              value={value || ""}
              onChange={onChange}
              hiddenLabel
              data-cy={fieldName}
              multiline={authType !== "password"}
              maxRows={7}
              minRows={7}
              data-testid={authType === "password" ? passwordTestid : passwordKeyTestid}
            />
          </Stack>
        );
      }}
    />
  );
};

type AuthTypeRadioGroupProps = {
  control?: Control;
  mode?: string;
  isRequired: boolean;
  setAuthType: (value: string) => void;
  authTypeFieldname: string;
};

const AuthTypeRadioGroup = ({ control, mode, isRequired, setAuthType, authTypeFieldname }: AuthTypeRadioGroupProps) => (
  <Controller
    key={authTypeFieldname}
    name={authTypeFieldname}
    control={control}
    rules={{ required: isRequired }}
    render={({ field: { onChange, value } }) => {
      return (
        <FormControl>
          <FormLabel>Authentication Type</FormLabel>
          <RadioGroup row name={authTypeFieldname} id={authTypeFieldname} value={value} onChange={onChange}>
            <Stack sx={{ alignItems: "center", flexDirection: "row" }}>
              <Typography sx={{ fontWeight: "bold", fontSize: "15px", marginRight: "10px" }}></Typography>
              <Stack sx={{ flexDirection: "row", marginTop: "5px" }}>
                <FormControlLabel
                  disabled={mode === "view"}
                  value="password"
                  control={<Radio value="password" checked={value === "password"} />}
                  label="Password"
                  data-cy="form-label-password"
                  onClick={() => setAuthType("password")}
                />
                <FormControlLabel
                  disabled={mode === "view"}
                  value="privateKey"
                  control={<Radio value="privateKey" checked={value === "privateKey"} />}
                  label="Private RSA Key"
                  data-cy="form-label-private-key"
                  onClick={() => setAuthType("privateKey")}
                />
              </Stack>
            </Stack>
          </RadioGroup>
        </FormControl>
      );
    }}
  />
);

type PassphraseInputProps = {
  credential?: Credential;
  mode?: string;
  control?: Control;
  passphraseFieldname: string;
};

const passphraseLabel = "Private Key Passphrase";

/** A private key generated with a passphrase is encrypted, and can only be read with that passphrase. */
const PassphraseInput = ({ credential, mode, control, passphraseFieldname }: PassphraseInputProps) => {
  if (mode === "view") {
    if (!credential?.properties_exist[passphraseFieldname]) return null;
    return <KeyCell label={passphraseLabel} value={dummyPassword} sx={{ paddingBottom: 0, marginTop: "16px" }} />;
  }

  return (
    <Controller
      key={passphraseFieldname}
      name={passphraseFieldname}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Stack spacing={1} sx={{ marginTop: "16px" }}>
          <Typography variant="body.medium.semibold">{`${passphraseLabel} (only if the key is encrypted)`}</Typography>
          <TextFieldStyled
            id={passphraseFieldname}
            placeholder={
              credential?.properties_exist[passphraseFieldname] ? dummyPassword : INPUT_PLACEHOLDERS.password
            }
            type="password"
            value={value || ""}
            onChange={onChange}
            hiddenLabel
            data-cy={passphraseFieldname}
            data-testid={passphraseFieldname}
          />
        </Stack>
      )}
    />
  );
};

type Sha1ConsentProps = {
  credential?: Credential;
  mode?: string;
  control?: Control;
  useSha1Fieldname: string;
};

const Sha1Consent = ({ credential, mode, control, useSha1Fieldname }: Sha1ConsentProps) => {
  const sha1Disclaimer = "I acknowledge using SHA-1, which is insecure.";

  if (mode === "view") {
    if (!credential?.properties[useSha1Fieldname]) return null;
    return (
      <Typography variant="body.medium.regular" sx={{ color: "text40.main", marginTop: "8px" }}>
        {sha1Disclaimer}
      </Typography>
    );
  }

  return (
    <Controller
      key={useSha1Fieldname}
      name={useSha1Fieldname}
      control={control}
      render={({ field: { value = false, onChange } }) => (
        <CheckboxWithLabel
          label={sha1Disclaimer}
          checked={!!value}
          onClick={() => {
            onChange(!value);
          }}
          sx={{ margin: "16px 0 0 0" }}
          disabled={mode === "view"}
          checkboxSx={{ marginRight: "8px" }}
        />
      )}
    />
  );
};

type PrivateKeyInputProps = {
  credential?: Credential;
  authType: string;
  control?: Control;
  mode?: string;
  errors?: FieldErrors;
  setAuthType: (value: string) => void;
  passwordTestid?: string;
  passwordFieldname: string;
  passwordKeyTestid?: string;
  privateKeyFieldname: string;
  authTypeFieldname: string;
  useSha1Fieldname: string;
  passphraseFieldname: string;
};

const PrivateKeyInput = ({
  authType,
  credential,
  control,
  mode,
  errors,
  setAuthType,
  passwordFieldname,
  privateKeyFieldname,
  authTypeFieldname,
  useSha1Fieldname,
  passphraseFieldname,
  passwordTestid,
  passwordKeyTestid,
}: PrivateKeyInputProps) => {
  const isRequired =
    authType === "password"
      ? !credential?.properties_exist[passwordFieldname]
      : !credential?.properties_exist[privateKeyFieldname];

  return (
    <>
      <AuthTypeRadioGroup
        control={control}
        isRequired={isRequired}
        mode={mode}
        setAuthType={setAuthType}
        authTypeFieldname={authTypeFieldname}
      />
      <KeyOrPasswordInput
        authType={authType}
        control={control}
        errors={errors}
        isRequired={isRequired}
        mode={mode}
        passwordFieldname={passwordFieldname}
        privateKeyFieldname={privateKeyFieldname}
        credential={credential}
        passwordTestid={passwordTestid || ""}
        passwordKeyTestid={passwordKeyTestid || ""}
      />
      {authType === "privateKey" && (
        <>
          <PassphraseInput
            credential={credential}
            mode={mode}
            control={control}
            passphraseFieldname={passphraseFieldname}
          />
          <Sha1Consent credential={credential} mode={mode} control={control} useSha1Fieldname={useSha1Fieldname} />
        </>
      )}
    </>
  );
};

export { PrivateKeyInput };
